import { Dispatch, SetStateAction, useCallback, useState } from 'react';

/**
 * An abstraction hook to toggle booleans
 *
 * @param initialValue Starting default value for the toggler (defaults to `false`)
 * @returns An array containing:
 *  1. The current value
 *  2. A toggle method to flips the current value
 *  3. A callback to set to a specific value
 *
 * @example
 * ```tsx
 * export function SomeComponent() {
 *  const [value, toggle, setValue] = useToggle();
 *
 *  // Just an example to use "setValue". This can be condition based.
 *  const customToggle = () => setValue((current) => !current);
 *
 *  return (
 *    <>
 *      Current value: <strong>{value.toString()}</strong>
 *      <Button onClick={toggle}>Toggle</Button>
 *      <Button onClick={customToggle}>Custom Toggle</Button>
 *      <Button onClick={() => setValue(true)}>Set value to true</Button>
 *      <Button onClick={() => setValue(false)}>set value to false</Button>
 *    </>
 *  );
 * }
 * ```
 */
export function useToggle(
    initialValue = false,
): [boolean, () => void, Dispatch<SetStateAction<boolean>>] {
    const [value, setValue] = useState(initialValue);

    const toggle = useCallback(() => setValue((current) => !current), []);

    return [value, toggle, setValue];
}
